import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { get } from 'lodash';
import ReactLoading from 'react-loading';

import Heading from 'components/Heading';
import Header from 'components/Header';
import Footer from 'components/Footer';
import FormGroup from 'components/Form/FormGroup';
import FormGroupLabel from 'components/Form/FormGroupLabel';
import { Grid, Column } from 'components/Layout';
import { Button } from 'components/Button';

import {
  nextAction,
  checkPreviousAction,
} from 'actions/workflow';
import { formatCurrency } from 'utils/formatCurrency';

import style from './style.scss';

const CreditOfferConfirmation = () => {
  const workflow = useSelector((state) => state.workflow);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const applicationId = get(workflow, 'attributes.entityId');
  const key = searchParams.get('key');
  const [isCheckinProgress, setIsCheckinProgress] = useState(!!key);
  const [workflowData, setWorkflowData] = useState(null);

  useEffect(() => {
    if (key) {
      dispatch(checkPreviousAction({
        data: {},
        url: `/workflows/fsl/${key}/checkin`,
        success: (response) => {
          const routeUrl = response.data && response.data.url;
          navigate(routeUrl);
          setIsCheckinProgress(false);
          setWorkflowData(get(response, 'data.data') || get(response, 'state.data'))
        },
        fail: () => {
          navigate(`/fsl/autopayelection?key=${key}`, { replace: true });
        },
      }));
    } else {
      navigate(`/fsl/autopayelection?key=${key}`, { replace: true });
    }
  }, []);

  const handleButtonClick = (e) => {
    e.preventDefault();

    if (key) {
      setIsLoading(true);
      dispatch(nextAction({
        data: {},
        url: `/workflows/fsl/${key}/next`,
        success: (response) => {
          setIsLoading(false);
          const routeUrl = response.data && response.data.url;
          navigate(routeUrl);
        },
        fail: (error) => {
          setIsLoading(false);
          console.log(error);
        },
      }));
    }
  };

  return (
    <Fragment>
      <Header />
      <section className="grid-container fluid borrowers-apply">
        <div className="grid-container">
          {isCheckinProgress ? (
            <Grid className="grid-margin-x max-limited">
              <Column className={cn('small-12 medium-12', style.alignCenter)}>
                <ReactLoading type="spin" color="#04bc6c" height={128} width={128} />
              </Column>
            </Grid>
          ) : (
            <Grid className="grid-margin-x max-limited">
              <Fragment>
                {applicationId ? (
                  <div className="cell small-12 large-offset-2 large-8" style={{ textAlign: 'left' }}>
                    <p className="mb-0 p-large"><b>Application ID:</b> {applicationId}</p>
                  </div>
                ) : null}
                <Heading
                  heading="Offer Confirmation"
                  isCardVisible={false}
                  className="large-offset-2 large-8"
                />
                <Column className="small-12 large-8 large-offset-2">
                  <FormGroup className="form-group">
                    <FormGroupLabel value="Confirm Amount of Sale" />
                    <Grid>
                      <div
                        className={cn('card loan-card', style['loan-card'], style.active, style.selected)}
                      >
                        <Grid>
                          <Column className={cn('small-12 medium-4 highlight', style.cell, style.highlight)}>
                            <h5>Estimated<br className="show-for-large" /> Monthly Payment</h5>
                            <h3>${get(workflowData, 'paymentAmount') ? formatCurrency(get(workflowData, 'paymentAmount'), 2) : ' -'}</h3>
                          </Column>
                          <Column className={cn('small-12 medium-8', style.cell, style.offerDetails)}>
                            <Grid>
                              <Column className="small-12 text-center">
                                <h3 className={style['mb-auto']} style={{ fontWeight: 700 }}>NO INTEREST</h3>
                                {get(workflowData, 'zipTerm') ? <p className={style['mb-1-rem']}>if paid within {get(workflowData, 'zipTerm')} months*</p> : null}
                              </Column>
                            </Grid>
                            <Grid>
                              <Column className="small-6">
                                <h5>Amount Financed</h5>
                              </Column>
                              <Column className="small-6">
                                <h5><em>${get(workflowData, 'payoutAmount') ? formatCurrency(get(workflowData, 'payoutAmount'), 2) :' -'}</em></h5>
                              </Column>
                            </Grid>
                            <Grid>
                              <Column className="small-6">
                                <h5>Loan Term</h5>
                              </Column>
                              <Column className="small-6">
                                <h5>{get(workflowData, 'term') ? <em>{get(workflowData, 'term')} Months</em> : '-'}</h5>
                              </Column>
                            </Grid>
                            <Grid>
                              <Column className="small-6">
                                <h5>Interest Rate</h5>
                              </Column>
                              <Column className="small-6">
                                <h5><em>{get(workflowData, 'rate') ? formatCurrency(get(workflowData, 'rate'), 2) : '- '}%</em></h5>
                              </Column>
                            </Grid>
                            <Grid>
                              <Column className="small-6">
                                <h5>APR</h5>
                              </Column>
                              <Column className="small-6">
                                <h5><em>{get(workflowData, 'apr') ? formatCurrency(get(workflowData, 'apr'), 4) : '- '}%</em></h5>
                              </Column>
                            </Grid>
                            <Grid>
                              <Column className="small-6">
                                <h5>Origination Fee**</h5>
                              </Column>
                              <Column className="small-6">
                                <h5><em>{get(workflowData, 'originationPercentage') ? formatCurrency(get(workflowData, 'originationPercentage') * 100, 0) : '- '}%</em></h5>
                              </Column>
                            </Grid>
                          </Column>
                        </Grid>
                      </div>
                      <Column className="small-12 medium-12">
                        <Button
                          className={cn('button large green', style.button)}
                          onClick={handleButtonClick}
                          isLoading={isLoading}
                        >
                          CONTINUE TO ESIGN
                        </Button>
                        <p className={style.description}>
                          Before your application can be finalized, LendingUSA and/or Cross River Bank will request a full credit report from one or more of the credit reporting agencies. This request will occur after you submit your completed application. This request will appear as a hard inquiry on your credit report and may affect your credit score.
                        </p>
                        <p className={style.description}>
                          * Your loan may have a No Interest on Principal Option Promotion included. This promotion can save you money if you pay off the principal amount of the loan in full within the Promotional Period (&quot;Promotional Period&quot;). During the Promotional Period you will be responsible for making all of your monthly payments and your loan will accrue interest on a monthly basis. If you pay off your loan within the Promotional Period, the monthly payments that you have made during this period, which includes accrued interest, will be deducted from the principal amount of the loan. Length of Promotional Periods vary, please review your loan agreement for full details.
                        </p>
                      </Column>
                      <Column className="small-12 medium-12">
                        <p className={style.description}>
                        ** The Origination Fee may be up to 8% of the Amount Financed, is non-refundable, and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for the offer listed above by multiplying the Amount Financed figure for the offer by .08 if the Origination Fee is 8%, by .06 if it is 6%, etc. For example, if the Origination Fee is 8% and if the Amount Financed is $1,000 the corresponding Origination Fee would be $80 ($1000 x .08 = $80). The total Principal Amount of your loan is the sum of the Amount Financed and the Origination Fee (i.e. if the Origination Fee is 8% and if the Amount Financed is $1,000 the Principal Amount of the loan would be $1080). Please review your Loan Agreement for additional terms and conditions.
                        </p>
                      </Column>
                    </Grid>
                  </FormGroup>
                </Column>
              </Fragment>
            </Grid>
          )}
        </div>
      </section>
      <Footer />
    </Fragment>
  );
};

export default CreditOfferConfirmation;
